import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Modal } from '@/shared/components/ui/Modal';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Button } from '@/shared/components/ui/Button';
import { useToast } from '@/shared/components/ui/Toast';
import { ConfirmDialog } from './ConfirmDialog';
import {
  useCreateNotificationSettings,
  useUpdateNotificationSettings,
  useDeleteNotificationSettings,
  useLocations,
} from '../hooks/useAdmin';
import type {
  NotificationSettingsWithLocation,
  NotificationType,
} from '../services/notificationSettingsService';

const notificationSchema = z.object({
  notification_type: z.enum([
    'FRAMES_PENDING',
    'FRAMES_ORDERED_NOT_RECEIVED',
    'LOCATION_INACTIVE',
  ] as const),
  enabled: z.boolean(),
  threshold_days: z.number().min(1).max(365),
  frequency: z.enum(['DAILY', 'WEEKLY'] as const),
  location_id: z.string().nullable(),
  recipient_emails: z.string().min(1, 'At least one email is required'),
  cc_regional: z.boolean(),
  regional_email: z.string().email('Invalid email').optional().or(z.literal('')),
});

type NotificationFormData = z.infer<typeof notificationSchema>;

interface NotificationFormProps {
  isOpen: boolean;
  onClose: () => void;
  notification?: NotificationSettingsWithLocation;
}

const NOTIFICATION_TYPE_OPTIONS: Array<{
  value: NotificationType;
  label: string;
}> = [
  { value: 'FRAMES_PENDING', label: 'Frames Pending' },
  {
    value: 'FRAMES_ORDERED_NOT_RECEIVED',
    label: 'Frames Ordered Not Received',
  },
  { value: 'LOCATION_INACTIVE', label: 'Location Inactive' },
];

export function NotificationForm({
  isOpen,
  onClose,
  notification,
}: NotificationFormProps) {
  const { showToast } = useToast();
  const isEditing = !!notification;
  const { data: locations } = useLocations();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<NotificationFormData>({
    resolver: zodResolver(notificationSchema),
  });

  const createMutation = useCreateNotificationSettings();
  const updateMutation = useUpdateNotificationSettings();
  const deleteMutation = useDeleteNotificationSettings();

  const ccRegional = watch('cc_regional');

  useEffect(() => {
    if (isOpen) {
      if (notification) {
        reset({
          notification_type: notification.notification_type,
          enabled: notification.enabled,
          threshold_days: notification.threshold_days,
          frequency: notification.frequency,
          location_id: notification.location_id,
          recipient_emails: notification.recipient_emails.join(', '),
          cc_regional: notification.cc_regional,
          regional_email: notification.regional_email || '',
        });
      } else {
        reset({
          notification_type: 'FRAMES_PENDING',
          enabled: true,
          threshold_days: 7,
          frequency: 'DAILY',
          location_id: null,
          recipient_emails: '',
          cc_regional: false,
          regional_email: '',
        });
      }
    }
  }, [isOpen, notification, reset]);

  const onSubmit = async (data: NotificationFormData) => {
    try {
      // Parse email addresses from comma-separated string
      const emails = data.recipient_emails
        .split(',')
        .map((e) => e.trim())
        .filter((e) => e.length > 0);

      const payload = {
        notification_type: data.notification_type,
        enabled: data.enabled,
        threshold_days: data.threshold_days,
        frequency: data.frequency,
        location_id: data.location_id || null,
        recipient_emails: emails,
        cc_regional: data.cc_regional,
        regional_email: data.regional_email || null,
      };

      if (isEditing && notification) {
        await updateMutation.mutateAsync({
          id: notification.id,
          enabled: payload.enabled,
          threshold_days: payload.threshold_days,
          frequency: payload.frequency,
          recipient_emails: payload.recipient_emails,
          cc_regional: payload.cc_regional,
          regional_email: payload.regional_email,
        });
        showToast('Notification settings updated successfully', 'success');
      } else {
        await createMutation.mutateAsync(payload);
        showToast('Notification settings created successfully', 'success');
      }
      onClose();
      reset();
    } catch (error) {
      console.error('Error saving notification settings:', error);
      showToast(
        error instanceof Error
          ? error.message
          : 'Failed to save notification settings',
        'error'
      );
    }
  };

  const handleClose = () => {
    onClose();
    reset();
  };

  const handleDelete = async () => {
    if (!notification) return;

    try {
      await deleteMutation.mutateAsync(notification.id);
      showToast('Notification settings deleted successfully', 'success');
      setShowDeleteDialog(false);
      onClose();
      reset();
    } catch (error) {
      console.error('Error deleting notification settings:', error);
      showToast(
        error instanceof Error
          ? error.message
          : 'Failed to delete notification settings',
        'error'
      );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={
        isEditing ? 'Edit Notification Settings' : 'Add Notification Settings'
      }
      size="md"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Select
          label="Notification Type"
          required
          disabled={isEditing}
          options={[
            { value: '', label: 'Select a type' },
            ...NOTIFICATION_TYPE_OPTIONS.map((opt) => ({
              value: opt.value,
              label: opt.label,
            })),
          ]}
          error={errors.notification_type?.message}
          {...register('notification_type')}
        />

        <Select
          label="Location"
          options={[
            { value: '', label: 'All Locations (Global)' },
            ...(locations?.map((loc) => ({
              value: loc.id,
              label: loc.name,
            })) || []),
          ]}
          error={errors.location_id?.message}
          {...register('location_id')}
          helperText="Leave as 'All Locations' for global notifications"
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Threshold (days)"
            type="number"
            required
            placeholder="7"
            error={errors.threshold_days?.message}
            {...register('threshold_days', { valueAsNumber: true })}
          />

          <Select
            label="Frequency"
            required
            options={[
              { value: 'DAILY', label: 'Daily' },
              { value: 'WEEKLY', label: 'Weekly' },
            ]}
            error={errors.frequency?.message}
            {...register('frequency')}
          />
        </div>

        <Input
          label="Recipient Emails"
          required
          placeholder="email1@example.com, email2@example.com"
          error={errors.recipient_emails?.message}
          {...register('recipient_emails')}
          helperText="Enter email addresses separated by commas"
        />

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="cc_regional"
            {...register('cc_regional')}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <label htmlFor="cc_regional" className="text-sm font-medium text-gray-700">
            CC Regional Manager
          </label>
        </div>

        {ccRegional && (
          <Input
            label="Regional Manager Email"
            type="email"
            placeholder="regional@example.com"
            error={errors.regional_email?.message}
            {...register('regional_email')}
          />
        )}

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="enabled"
            {...register('enabled')}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <label htmlFor="enabled" className="text-sm font-medium text-gray-700">
            Enabled
          </label>
        </div>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-3 pt-4 border-t">
          <div>
            {isEditing && (
              <Button
                type="button"
                variant="danger"
                onClick={() => setShowDeleteDialog(true)}
                className="w-full sm:w-auto"
              >
                Delete
              </Button>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button type="button" variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : isEditing ? 'Update' : 'Create'}
            </Button>
          </div>
        </div>
      </form>

      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDelete}
        title="Delete Notification Settings"
        message="Are you sure you want to delete these notification settings? This action cannot be undone."
        confirmLabel="Delete"
        confirmVariant="danger"
        isLoading={deleteMutation.isPending}
      />
    </Modal>
  );
}
